export type ComposerHistoryDirection = 'older' | 'newer';

export interface ComposerHistoryKeyIntent {
  readonly key: string;
  readonly altKey?: boolean;
  readonly ctrlKey?: boolean;
  readonly metaKey?: boolean;
  readonly shiftKey?: boolean;
  readonly isComposing?: boolean;
}

interface ScopeHistory {
  items: string[];
  cursor: number | null;
  draft: string;
}

const HISTORY_LIMIT = 50;

/**
 * Only claim unmodified Up/Down at a textarea boundary. Cursor movement inside a draft,
 * browser shortcuts and IME composition remain native.
 */
export function composerHistoryDirection(
  event: ComposerHistoryKeyIntent,
  selectionStart: number,
  selectionEnd: number,
  valueLength: number,
  walking = false,
): ComposerHistoryDirection | null {
  if (event.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return null;
  if (walking && selectionStart === selectionEnd && event.key === 'ArrowUp') return 'older';
  if (walking && selectionStart === selectionEnd && event.key === 'ArrowDown') return 'newer';
  if (event.key === 'ArrowUp' && selectionStart === 0 && selectionEnd === 0) return 'older';
  if (event.key === 'ArrowDown' && selectionStart === valueLength && selectionEnd === valueLength) return 'newer';
  return null;
}

/**
 * Tab-memory only. Entries never reach localStorage and are partitioned by the caller's
 * organization/project or Worker/session scope.
 */
export class ComposerHistory {
  private readonly scopes = new Map<string, ScopeHistory>();
  private activeScope: string | null = null;

  record(scope: string, text: string): void {
    const sent = text.trim();
    if (scope === '' || sent === '') return;
    const state = this.scope(scope);
    state.items = [sent, ...state.items.filter((item) => item !== sent)].slice(0, HISTORY_LIMIT);
    state.cursor = null;
    state.draft = '';
    this.activeScope = scope;
  }

  recall(scope: string, direction: ComposerHistoryDirection, currentDraft: string): string | null {
    if (scope === '') return null;
    const state = this.activate(scope);
    if (state.items.length === 0) return null;

    if (direction === 'older') {
      if (state.cursor === null) {
        state.draft = currentDraft;
        state.cursor = 0;
      } else {
        state.cursor = Math.min(state.cursor + 1, state.items.length - 1);
      }
      return state.items[state.cursor] ?? null;
    }

    if (state.cursor === null) return null;
    if (state.cursor === 0) {
      state.cursor = null;
      return state.draft;
    }
    state.cursor -= 1;
    return state.items[state.cursor] ?? null;
  }

  isWalking(scope: string): boolean {
    return this.activeScope === scope && this.scope(scope).cursor !== null;
  }

  /** A user edit leaves history navigation while keeping the edited text in the composer. */
  edit(scope: string): void {
    if (scope === '') return;
    const state = this.scope(scope);
    state.cursor = null;
    state.draft = '';
    this.activeScope = scope;
  }

  private activate(scope: string): ScopeHistory {
    const state = this.scope(scope);
    if (this.activeScope !== scope) {
      state.cursor = null;
      state.draft = '';
      this.activeScope = scope;
    }
    return state;
  }

  private scope(scope: string): ScopeHistory {
    const existing = this.scopes.get(scope);
    if (existing !== undefined) return existing;
    const created: ScopeHistory = { items: [], cursor: null, draft: '' };
    this.scopes.set(scope, created);
    return created;
  }
}
